import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * newsletter — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Newsletter" section
 * (shrine-pro-ref/sections/newsletter.liquid, with the `.newsletter` / `.newsletter__wrapper` /
 * `.newsletter-form` / `.field` / `.button` CSS ported VERBATIM from assets/base.css). It is the email-capture
 * band: a highlightable HEADING over a SUBHEADING over a real `<form>` — Shopify's signature floating-label
 * email input (`.field` → `.field__input` + `.field__label`) followed by either a full-width SOLID button or
 * an inline ARROW button. The colored box sits inside `content-container` and flips palette via the 6 Shrine
 * color schemes (+ a full custom palette: bg / gradient / image / text / solid-button colors).
 *
 * Field-type mapping (Shopify → ours, same convention as the collapsible-content / collage rebuilds):
 * inline_richtext/richtext → richtext (bold markup → the `title-with-highlight` engine), text → text,
 * select → select, checkbox → checkbox, color → color, color_background → gradient, image_picker → image,
 * range → range, header → group, visible_if → visibleWhen. Ids are clean camelCase; labels / options /
 * defaults stay faithful to Shrine (button_type, button_label "Sign up", button_style_secondary, the 6
 * schemes + custom palette, the 13-pattern background, full_width default on, padding 40 / 52).
 *
 * MODELING NOTE: Shrine ships heading / paragraph / email_form as three limit-1 BLOCKS (so they can be
 * reordered / removed). A newsletter is a FIXED unit — one heading, one subheading, one capture form, in that
 * order — and our storefront does not run Shopify's per-block `--index` reveal animation, so the block model
 * buys nothing here. We expose the SAME knobs as SECTION-LEVEL FIELDS (cleaner inspector, exact rendered
 * output); clearing the heading / subheading richtext hides that piece, matching "remove the block".
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged): (a) a real subscribe POST — v1 validates + shows the
 * success state client-side, no backend endpoint exists yet (same stub as contact-form); (b) Shrine's block
 * reorder/remove for the 3 pieces (see MODELING NOTE); (c) the arrow-button `:focus-visible` inversion chrome;
 * (d) the compound `visible_if` on customMobileImageBackground — Shrine gates it on
 * `color_scheme == custom AND custom_image_background != blank`, but our single-field VisibleWhen DSL can only
 * express the `custom` half, so the mobile-image field shows a touch early (same DSL limit as collapsible-content;
 * closing it needs a DSL extension, not a per-section change — the Component's mobile-image CSS already no-ops
 * when no desktop image is set, so it's inspector-only).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const CONTENT = 'Content';
const GENERAL = 'General';
const COLOR = 'Color';
export const newsletterSchema = {
    type: 'newsletter',
    name: 'Newsletter',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Content: heading (+ highlight color + size) + subheading ─────────────────
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: CONTENT,
            default: { format: 'html', html: '<p>Subscribe to our emails</p>' },
            info: 'Bold (B) a word to highlight it with the color below.',
        },
        {
            id: 'titleHighlightColor',
            type: 'color',
            label: 'Heading highlight color',
            group: CONTENT,
            default: { ref: 'theme', token: 'accent' },
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: CONTENT,
            default: 'h1',
            options: [
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
            ],
        },
        {
            id: 'subheading',
            type: 'richtext',
            label: 'Subheading',
            group: CONTENT,
            default: {
                format: 'html',
                html: '<p>Be the first to know about new collections and exclusive offers.</p>',
            },
        },
        // ── General: the submit button (Shrine "General" header) ─────────────────────
        {
            id: 'buttonType',
            type: 'select',
            label: 'Submit button type',
            group: GENERAL,
            default: 'solid',
            options: [
                { value: 'arrow', label: 'Arrow button' },
                { value: 'solid', label: 'Solid button' },
            ],
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Solid button label',
            group: GENERAL,
            default: 'Sign up',
            defaultKey: 'newsletter.subscribe',
            visibleWhen: { field: 'buttonType', equals: 'solid' },
        },
        {
            id: 'buttonStyleSecondary',
            type: 'checkbox',
            label: 'Use outline button style',
            group: GENERAL,
            default: false,
            visibleWhen: { field: 'buttonType', equals: 'solid' },
        },
        // ── Color: the 6 Shrine schemes + a full custom palette (same engine as collage) ─
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: COLOR,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: COLOR,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: COLOR,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: COLOR,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: COLOR,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: COLOR,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: COLOR,
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customButtonText',
            type: 'color',
            label: 'Custom solid button label color',
            group: COLOR,
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared 13-pattern engine) ─────────────────────────────
        ...patternFields('Background pattern'),
        // ── Layout ────────────────────────────────────────────────────────────────────
        {
            id: 'fullWidth',
            type: 'checkbox',
            label: 'Make section full width',
            group: 'Layout',
            default: true,
        },
        // ── Section padding (Shrine defaults 40 / 52) ─────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 52,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
};
export const newsletterPresets = [
    {
        name: 'Newsletter',
        settings: {
            heading: { format: 'html', html: '<p>Subscribe to our emails</p>' },
            headingSize: 'h1',
            titleHighlightColor: { ref: 'theme', token: 'accent' },
            subheading: {
                format: 'html',
                html: '<p>Be the first to know about new collections and exclusive offers.</p>',
            },
            buttonType: 'solid',
            colorScheme: 'background-1',
            fullWidth: true,
            paddingTop: 40,
            paddingBottom: 52,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const newsletterMeta = {
    label: 'Newsletter',
    group: 'Content',
    icon: 'type',
};
//# sourceMappingURL=schema.js.map